import axios from "axios";
import type { ApiResponse } from "@/types/api";
import { getClient, getApiBaseUrl } from "@/services/ApiService";

export interface PatientFacilityInfo {
  facilityID: number;
  officeName: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  openEdge: boolean;
  fortis: boolean;
  allowApptBooking: boolean;
}

export interface PatientCommOptions {
  patientID: number;
  mobilePhone: string;
  homePhone: string;
  email: string;
  sendSms: boolean;
  sendEmail: boolean;
  callMobilePhone: boolean;
  callHomePhone: boolean;
  otherEmail: boolean;
  twoWaySms: boolean;
  facilityId?: number | string;
}

export interface PatientCommOptionsResponse {
  data: PatientCommOptions;
}

const secured = () => getClient();

const authRequest = {
  request: {
    async validateUser(payload: {
      username: string;
      password: string;
    }): Promise<ApiResponse> {
      const manualReq = axios.create({
        baseURL: getApiBaseUrl(),
        headers: { "Content-Type": "application/json" },
      });
      return manualReq.post("/Portal/ValidateUser", payload);
    },

    async updateUser(payload: {
      id: string;
      firstName: string;
      lastName: string;
      nickName: string;
    }): Promise<ApiResponse> {
      return secured().post(`/Portal/UpdateUser/${payload.id}`, payload);
    },

    async updateUserEmail(payload: {
      id: string;
      email: string;
      pwd: string;
    }): Promise<ApiResponse> {
      return secured().post("/PatientPortal/UpdateUserEmail", payload);
    },

    async updateUserMobilephone(payload: {
      id: string;
      mobilePhone: string;
    }): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdateUserMobilePhone/${encodeURIComponent(payload.id)}/${encodeURIComponent(payload.mobilePhone)}`
      );
    },

    async updateUserCommoptions(payload: {
      id: string;
      canEmail: boolean;
      canSms: boolean;
    }): Promise<ApiResponse> {
      return secured().post("/Portal/UpdateUserCommOptions", payload);
    },

    async updateUserPwd(payload: { id: string; pwd: string }): Promise<ApiResponse> {
      return secured().post(`/Portal/UpdateUserPwd/${payload.id}`, payload);
    },

    async getUserWithToken(
      payload: { email: string },
      apiToken: string
    ): Promise<ApiResponse> {
      const manualReq = axios.create({
        baseURL: getApiBaseUrl(),
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + apiToken,
        },
      });
      return manualReq.post("/Portal/GetUser", payload);
    },

    async getUser(payload: { email: string }): Promise<ApiResponse> {
      return secured().get(`/Portal/GetUser/${encodeURIComponent(payload.email)}`);
    },

    async getUserPatients(payload: string): Promise<ApiResponse> {
      return secured().get(`/Portal/GetUserPatients/${encodeURIComponent(payload)}`);
    },

    async getPatientCommOptions(payload: {
      facilityKey: string;
      patientKey: string;
    }): Promise<PatientCommOptionsResponse> {
      return secured().post(
        `/Portal/GetPatientCommOptions/${encodeURIComponent(payload.facilityKey)}/${encodeURIComponent(payload.patientKey)}`,
        {}
      );
    },

    async updatePatientCommOptions(payload: {
      facilityKey: string;
      patientKey: string;
      sendSms: number;
      sendEmail: number;
      callMobilePhone: number;
      callHomePhone: number;
      twoWaySms: boolean;
      otherEmail: boolean;
      mobilePhone: string;
      homePhone: string;
      email: string;
    }): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdatePatientCommOptions/${encodeURIComponent(payload.facilityKey)}/${encodeURIComponent(payload.patientKey)}`,
        payload
      );
    },

    async getLink(payload: string): Promise<ApiResponse> {
      return secured().get(`/PatientPortal/GetLink/${encodeURIComponent(payload)}`);
    },

    async updatePatient(payload: {
      patientPortalUserID: string;
      facilityKey: string;
      patientKey: string;
      firstName: string;
      lastName: string;
    }): Promise<ApiResponse> {
      return secured().post(
        `/Portal/UpdatePatient/${encodeURIComponent(payload.facilityKey)}/${encodeURIComponent(payload.patientKey)}`,
        payload
      );
    },

    /** Portal expects facilityKey (not facilityId). Call with getFacilityKeyForPortal() only. */
    async getFacilityInfo(facilityKey: string): Promise<ApiResponse<PatientFacilityInfo>> {
      return secured().get(
        `/Portal/GetFacilityInfo/${encodeURIComponent(facilityKey)}`
      );
    },

    async resetUserPassword(payload: {
      email: string;
    }): Promise<{ success: boolean; reason: string }> {
      const manualReq = axios.create({
        baseURL: getApiBaseUrl(),
        headers: { "Content-Type": "application/json" },
      });
      return (
        await manualReq.post("/Portal/ResetUserPwd", payload)
      ).data as { success: boolean; reason: string };
    },
  },
};

export default authRequest;
